import type { AuthTokens, PublicConfig } from "@mantra/shared";
import { kvDel, kvGet, kvSet } from "./db";
import { telegram } from "./telegram";

/** Пусто — тот же origin (PWA за nginx). В Capacitor — абсолютный URL. */
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

interface Session {
  accessToken: string;
  refreshToken: string;
  userId: string;
}

let session: Session | null = null;
let sessionPromise: Promise<Session> | null = null;

function randomKey(): string {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function raw<T>(path: string, init: RequestInit & { json?: unknown } = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, err?.code ?? "http_error", err?.message ?? res.statusText);
  }
  return body as T;
}

async function store(t: AuthTokens): Promise<Session> {
  session = { accessToken: t.accessToken, refreshToken: t.refreshToken, userId: t.user.id };
  await kvSet("session", session);
  await kvSet("me", t.user);
  return session;
}

/**
 * Вход без экрана логина: секрет устройства → анонимный аккаунт.
 * Внутри Telegram дополнительно привязываем Telegram — данные переедут при входе с другого устройства.
 */
async function login(): Promise<Session> {
  let key = await kvGet<string>("deviceKey");
  if (!key) {
    key = randomKey();
    await kvSet("deviceKey", key);
  }
  let s = await store(await raw<AuthTokens>("/v1/auth/device", { method: "POST", json: { deviceKey: key } }));
  const initData = telegram()?.initData;
  if (initData) {
    try {
      s = await store(await raw<AuthTokens>("/v1/auth/telegram", { method: "POST", json: { initData } }, s.accessToken));
    } catch {
      /* Telegram-вход не настроен на сервере — остаёмся анонимными */
    }
  }
  return s;
}

async function getSession(): Promise<Session> {
  if (session) return session;
  sessionPromise ??= (async () => (await kvGet<Session>("session")) ?? login())().finally(() => (sessionPromise = null));
  session = await sessionPromise;
  return session;
}

async function refresh(): Promise<Session> {
  const current = await getSession();
  try {
    return await store(await raw<AuthTokens>("/v1/auth/refresh", { method: "POST", json: { refreshToken: current.refreshToken } }));
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      session = null;
      await kvDel("session");
      return login();
    }
    throw e;
  }
}

let refreshing: Promise<Session> | null = null;

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const s = await getSession();
  try {
    return await raw<T>(path, init, s.accessToken);
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) throw e;
    refreshing ??= refresh().finally(() => (refreshing = null));
    const fresh = await refreshing;
    return raw<T>(path, init, fresh.accessToken);
  }
}

export async function getConfig(): Promise<PublicConfig | undefined> {
  try {
    const c = await raw<PublicConfig>("/v1/config", { cache: "no-store" });
    await kvSet("config", c);
    return c;
  } catch {
    return kvGet<PublicConfig>("config");
  }
}

export const isOnline = () => navigator.onLine !== false;
